package fr.frux.feature.map

import dev.zacsweers.metro.Inject
import fr.frux.core.common.logging.Logger
import fr.frux.core.common.prefs.AppPreferences
import fr.frux.core.common.result.runCatchingCancellable
import kotlin.time.Instant

/** Cle du dernier horodatage de sollicitation. Une seule cle : le plafond est global, pas par arbre. */
internal const val NEVER_SEEN_INVITE_LAST_SHOWN_KEY = "never_seen_invite_last_shown_at"

/**
 * Memoire du plafond de sollicitation de l'invitation « personne n'est encore passe » (#396).
 *
 * Persistante et non de session : rouvrir l'app trois fois de suite ne doit pas redonner trois
 * invitations, c'est la seule protection contre un mecanisme qui deviendrait du harcelement. Le
 * plafond vit donc au-dela du lancement, la ou un drapeau en memoire serait remis a zero.
 *
 * Toute erreur de stockage est avalee, comme dans [MapViewStore] : ne pas se souvenir d'une
 * sollicitation est un desagrement, ne pas pouvoir ouvrir la carte est une panne. Une lecture qui
 * echoue rend `null`, donc l'invitation reste possible - le sens de repli le moins genant serait
 * discutable dans l'autre direction, mais un stockage casse ne doit pas eteindre la fonction
 * definitivement. L'annulation de coroutine traverse (cf. [runCatchingCancellable]).
 */
@Inject
class NeverSeenInviteStore(
    private val preferences: AppPreferences,
) {
    suspend fun lastShownAt(): Instant? {
        val raw =
            runCatchingCancellable { preferences.getString(NEVER_SEEN_INVITE_LAST_SHOWN_KEY) }
                .onFailure { Logger.e(TAG, "Lecture du plafond d'invitation impossible", it) }
                .getOrNull()

        // Une valeur ecrite par une version anterieure du format, ou tronquee, se traite comme
        // absente plutot que de faire echouer le chemin.
        return raw?.toLongOrNull()?.let { Instant.fromEpochMilliseconds(it) }
    }

    suspend fun markShown(at: Instant) {
        runCatchingCancellable {
            preferences.putString(NEVER_SEEN_INVITE_LAST_SHOWN_KEY, at.toEpochMilliseconds().toString())
        }.onFailure { Logger.e(TAG, "Ecriture du plafond d'invitation impossible", it) }
    }

    private companion object {
        const val TAG = "NeverSeenInviteStore"
    }
}
